import { DriveStore } from "../../store/reducers";
import DriveApi from "../api";
import { ICredentials } from "../credentialsStore";
import { UserService } from "../users";
import { TApiCredentialsCallback, TApiErrorCallback, TApiSuccessCallback } from "../api.interfaces";
import { IFeatureFlagsService } from "../../services/featureFlags";
import { driveAppAuthLogout, getToken, isGetTokenModule } from "native-sdk";
import { IIamSettings } from "./iamSettings";
import { TContextSource } from "../../app.manager";
import * as logger from "../logger";
import constants from "../../constants";
import { unwrapFirstUser } from "./unwrapFirstUser";

const protectedPagesRegex = new RegExp(`(${ constants.IAM.PROTECTED_PAGES.map(p => p.replace("/", "\/")).join("|") })`);
const invalidSessionError = (method: string, params: any) => {
    return {
        message: "Invalid session",
        code: -32000,
        data: {
            id: "",
            type: "InvalidUserException",
            requestIndex: 0,
            info: {
                apiCall: {
                    method: method,
                    params: params
                }
            }
        }
    };
};

export default class NewIamApi extends DriveApi {

    // sessionInvalidated is used to track if the session has been invalidated
    // but the user is unable to logout due to certain conditions (e.g. being on a protected page, lock screen is up, currently driving, or not using the UI)
    private sessionInvalidated = false;
    constructor(
        userService: UserService,
        store: DriveStore,
        protected userName?: string,
        readonly source?: TContextSource,
        readonly featureFlagService?: IFeatureFlagsService
    ) {
        super(userService, store, userName, featureFlagService);
        this.updateTransportWithHeaders();
    }

    logUserOutIfAllowed(method: string, params: any, callbackError?: TApiErrorCallback) {
        if (!this.sessionInvalidated) {
            return;
        }

        if (this.allowToLogout()) {
            this.forget();
            this.sessionInvalidated = false;
        }

        callbackError && callbackError(invalidSessionError(method, params));
    }

    allowToLogout() {
        const mobileStore = this.store.getState().mobile;
        const currentPage = mobileStore.currentPage || "";
        const isLocked = mobileStore.pageState.isLocked;
        const userName = this.getUser()?.name || "";

        if (userName && !this.userService.isDriverUsingUI(userName)) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: User not using the UI, not allowed to logout`, this.getUser());
            return false;
        }

        if (isLocked) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: Lock-screen is up, not allowed to logout`, this.getUser());
            return false;
        }

        if (protectedPagesRegex.test(currentPage)) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: User on a protected page, not allowed to logout`, this.getUser());
            return false;
        }

        if (this.store.getState().data.driving) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: User is currently driving, not allowed to logout`, this.getUser());
            return false;
        }

        return true;
    }

    call(method: string, params: any, callbackSuccess?: TApiSuccessCallback, callbackError?: TApiErrorCallback, ignoreCredentials?: boolean, binaryData?: ArrayBuffer, binaryMimeType?: string) {
        if (this.sessionInvalidated) {
            this.logUserOutIfAllowed(method, params, callbackError);
            return;
        }
        this.updateTransportWithHeaders().then(() => super.call(method, params, callbackSuccess, callbackError, ignoreCredentials, binaryData, binaryMimeType));
    }

    callPromise(method: string, params: any, ignoreCredentials?: boolean) {
        return super.callPromise(method, params, ignoreCredentials);
    }

    multiCall(calls: any[], callbackSuccess: TApiSuccessCallback, callbackError: TApiErrorCallback, ignoreCredentials = false) {
        let formattedCalls = calls.map(call => {
            return {
                method: call[0],
                params: call[1]
            };
        });

        if (this.sessionInvalidated) {
            this.logUserOutIfAllowed("ExecuteMultiCall", { formattedCalls }, callbackError);
            return;
        }

        this.updateTransportWithHeaders().then(() => super.multiCall(calls, callbackSuccess, callbackError, ignoreCredentials));
    }

    multiCallPromise(calls: any[], ignoreCredentials = false) {
        return super.multiCallPromise(calls, ignoreCredentials);
    }

    getSession(successCallback: TApiCredentialsCallback | null, newSession?: boolean) {
        return super.getSession(successCallback, newSession);
    }

    updateTransportWithHeaders(credentials?: ICredentials) {
        const creds = credentials ?? this.getCurrentCredentials()?.credentials;
        if (!creds || !creds.userName || !isGetTokenModule()) {
            return Promise.resolve();
        }

        return new Promise<void>(resolve => {
            getToken(creds.userName, (error, token) => {
                try {
                    if (token) {
                        const headers = [
                            { name: "Authorization", value: `Bearer ${ token.accessToken }` },
                            { name: "UserName", value: creds.userName }
                        ];

                        if (creds?.database) {
                            headers.push({ name: "Database", value: creds.database });
                        }
                        this.setHeaders(headers);
                        this.sessionInvalidated = false;
                        return;
                    }

                    logger.error(logger.Source.IAM, `AppAuth getToken: native sdk error: ${ error?.message ?? "unknown" }`, this.getUser());
                    if (error?.shouldRedirectToLogin) {
                        if (this.allowToLogout()) {
                            this.forget();
                        } else {
                            this.sessionInvalidated = true;
                        }
                    }
                } catch (err) {
                    logger.error(logger.Source.IAM, `AppAuth getToken: error in updateTransportWithHeaders callback: ${ err }`, this.getUser());
                } finally {
                    resolve();  // Always resolve to prevent hanging
                }
            });
        });
    }

    setUser(credentials: ICredentials, server: string, iamSettings?: IIamSettings) {
        super.setUser(credentials, server, iamSettings);
        this.updateTransportWithHeaders(credentials);
    }

    async getUserDatabaseNames(userName: string): Promise<string[]> {
        // Database is not optional in ICredentials, but we don't know it yet, so we pass a dummy value
        // GetDatabaseNames does not require the database in the header, but doesn't break if it's there
        return this.updateTransportWithHeaders({ userName, database: "_no_database_"})
            .then(() => this.callPromise("GetDatabaseNames", {}));
    }

    async callUpsertUser(userName: string, database: string) {
        return this.updateTransportWithHeaders({ userName, database })
            .then(() => this.callPromise("UpsertMyAdminUser", { userName }));
    }

    async checkUserExist(userName: string, database: string) {
        return this.updateTransportWithHeaders({ userName, database })
            .then(() => this.callPromise("Get", {
                typeName: "User",
                search: { name: userName }
            }))
            .then(unwrapFirstUser);
    }

    destroy() {
        if (this.source === "getContext" && this.userName) {
            driveAppAuthLogout(this.userName, () => {
                logger.log(logger.Source.IAM, "AppAuth driveAppAuthLogout: completed", this.userName);
                super.destroy();
            });
            return;
        }
        super.destroy();
    }
}